import { useMemo } from 'react';

import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core/useDialogs';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';

import { ApiError } from 'api';
import {
  getStudentSelfScheduleEligibilityOptions,
  getStudentScheduleOptions,
  listSelfSchedulePeriodsOptions,
  listStudentCourseEnrollmentsOptions,
  cancelPeriodEnrollmentMutation,
  enrollStudentInPeriodMutation,
  enrollStudentInPeriodWaitlistMutation,
  countSelfSchedulePeriodsQueryKey,
  listSelfSchedulePeriodsQueryKey,
  getStudentScheduleQueryKey,
  getStudentSelfScheduleEligibilityQueryKey,
} from '@drivingdoc/api-client/generated/react-query';
import {
  StudentCourseEnrollment,
  StudentEligibilityResponse,
  SelfScheduledPeriod,
  StudentPeriodEnrollment,
} from 'types/students';

import FeedbackDialog from 'components/Dialogs/FeedbackDialog';

import useAuthContext from 'hooks/useAuthContext';
import useSelfScheduleContext from 'hooks/useSelfScheduleContext';

import {
  doesBlockAffectPeriod,
  generateSummaryBalanceBlock,
  prioritizeBlocks,
} from 'utils/eligibilityHelpers';

import StudentPage from '../components/StudentPage';
import StudentPeriod from '../StudentDashboard/components/StudentPeriod/StudentPeriod';
import EligibilityBlockAlert from './components/EligibilityBlockAlert';
import PeriodListHeader from './components/PeariodListHeader';
import SelfScheduleMenu from './components/SelfScheduleMenu';
import SelfSchedulePeriod from './components/SelfSchedulePeriod';
import { CountSelfSchedulePeriodsData, GetStudentScheduleData, ListSelfSchedulePeriodsData, Options } from '@drivingdoc/api-client/generated';

dayjs.extend(advancedFormat);
dayjs.extend(duration);

// error codes
// PERIOD_NOT_FOUND
// STUDENT_NOT_FOUND
// COURSE_PERIOD_FULL
// STUDENT_NOT_ENROLLED_IN_COURSE
// COURSE_SSP_MISSING
//
// SSP_VOILATION_MAX_PERIODS_IN_INTERVAL
// SSP_VOILATION_MAX_PERIODS_IN_ADVANCE
// SSP_VOILATION_MISSING_PERMIT
// SSP_VOILATION_MISSING_PERMIT_EXP
// SSP_VOILATION_PERMIT_EXP
// ALREADY_ENROLLED_IN_PERIOD

function SelfSchedule(): JSX.Element {
  // get the student id from the auth context
  const { userId: studentId } = useAuthContext();

  const dialogs = useDialogs();

  // get menu state from self schedule context
  const {
    selfScheduleParams: { selectedDate, selectedEnrollment },
  } = useSelfScheduleContext();
  const queryClient = useQueryClient();

  // get performance and observation time left to schedule
  const performanceTimeRemaining = dayjs
    .duration(selectedEnrollment?.performance_time_included ?? { minutes: 0 })
    .subtract(dayjs.duration(selectedEnrollment?.performance_time_scheduled ?? { minutes: 0 }));
  const observationTimeRemaining = dayjs
    .duration(selectedEnrollment?.course_completion_criteria?.observation_time_required ?? {})
    .subtract(selectedEnrollment?.observation_time_scheduled ?? {});

  console.log(`Performance Time Remaining: ${performanceTimeRemaining.asMinutes()} minutes`);
  console.log(`Observation Time Remaining: ${observationTimeRemaining.asMinutes()} minutes`);

  // fetch student course enrollments and count how many self scheduled courses the student is enrolled in
  const courseEnrollments = useQuery({
    ...listStudentCourseEnrollmentsOptions({
      path: {
        id: studentId ?? '',
      },
    }),
    enabled: !!studentId,
  });
  const courseEnrollmentData = courseEnrollments.data as StudentCourseEnrollment[] | undefined;
  const selfScheduleCourseCount = useMemo(
    () => courseEnrollmentData?.filter((enrollment) => enrollment?.course?.schedule_type === 'self_scheduled')?.length ?? 0,
    [courseEnrollmentData],
  );

  // fetch student period enrollments
  const periodEnrollments = useQuery({
    ...getStudentScheduleOptions({
      path: {
        id: studentId ?? '',
      },
    }),
    enabled: !!studentId,
  });

  // create a map of course period id to student period enrollment
  const studentPeriods = useMemo(() => {
    return (periodEnrollments?.data as StudentPeriodEnrollment[] | undefined)?.reduce(
      (map, enrollment) => map.set(enrollment.course_period_id, enrollment),
      new Map<string, StudentPeriodEnrollment>(),
    );
  }, [periodEnrollments]);

  // get all self scheduled periods for the seleceted enrollment
  const selfSchedulePeriods = useQuery({
    ...listSelfSchedulePeriodsOptions({
      path: {
        id: studentId ?? '',
        courseEnrollmentId: selectedEnrollment?.id ?? '',
      },
      query: {
        start: selectedDate.toISOString(),
        end: selectedDate.add(24, 'hours').toISOString(),
      },
    }),
    enabled: !!selectedEnrollment?.id,
  });
  const selfSchedulePeriodData = selfSchedulePeriods.data as SelfScheduledPeriod[] | undefined;

  // fetch eligibility data for the selected enrollment
  const eligibility = useQuery({
    ...getStudentSelfScheduleEligibilityOptions({
      path: {
        id: studentId ?? '',
        courseEnrollmentId: selectedEnrollment?.id ?? '',
      },
      query: {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    }),
    enabled: !!studentId && !!selectedEnrollment?.id,
  });
  const eligibilityData = eligibility.data as StudentEligibilityResponse | undefined;

  // Determine if student has booked their first period (any time scheduled)
  const hasBookedFirstPeriod = useMemo(() => {
    const performanceMinutes = dayjs.duration(selectedEnrollment?.performance_time_scheduled ?? { minutes: 0 }).asMinutes();
    const observationMinutes = dayjs.duration(selectedEnrollment?.observation_time_scheduled ?? { minutes: 0 }).asMinutes();
    return performanceMinutes > 0 || observationMinutes > 0;
  }, [selectedEnrollment?.performance_time_scheduled, selectedEnrollment?.observation_time_scheduled]);

  // Generate eligibility blocks for the menu including the summary balance block
  const menuEligibilityBlocks = useMemo(() => {
    const blocks = eligibilityData?.blocks ?? [];

    // Generate summary balance block if applicable
    if (eligibilityData?.currentBalance !== undefined && eligibilityData?.balanceRestrictions) {
      const summaryBlock = generateSummaryBalanceBlock({
        currentBalance: eligibilityData.currentBalance,
        balanceRestrictions: eligibilityData.balanceRestrictions,
        hasBookedFirstPeriod,
      });

      if (summaryBlock) {
        return [...blocks, summaryBlock];
      }
    }

    return blocks;
  }, [eligibilityData?.blocks, eligibilityData?.currentBalance, eligibilityData?.balanceRestrictions, hasBookedFirstPeriod]);

  // Filter blocks to only show those affecting the selected date (for display above period list)
  const blocksForSelectedDate = useMemo(() => {
    const allBlocks = eligibilityData?.blocks ?? [];

    // Filter to only blocks that affect the selected date
    const dayStart = selectedDate.startOf('day');
    const dayEnd = selectedDate.endOf('day');

    return allBlocks.filter((block) => {
      // Skip indefinite blocks that are shown in the menu
      if (block.type === 'blocked_indefinitely') return false;

      return doesBlockAffectPeriod(block, dayStart.toISOString(), dayEnd.toISOString());
    });
  }, [eligibilityData?.blocks, selectedDate]);

  // get the number of openings for the selected date
  const numOpenings = useMemo(
    () =>
      selfSchedulePeriodData?.filter((period) => period?.enrollment_performance_count < period?.capacity).length ??
      0,
    [selfSchedulePeriodData],
  );

  // Shared function to invalidate queries after enrollment changes
  const invalidateSelfScheduleQueries = () => {
    queryClient.invalidateQueries({
      queryKey: countSelfSchedulePeriodsQueryKey({} as Options<CountSelfSchedulePeriodsData>),
      exact: false,
    });
    queryClient.invalidateQueries({
      queryKey: listSelfSchedulePeriodsQueryKey({} as Options<ListSelfSchedulePeriodsData>),
      exact: false,
    });
    queryClient.invalidateQueries({
      queryKey: getStudentScheduleQueryKey({} as Options<GetStudentScheduleData>),
      exact: false,
    });
    queryClient.invalidateQueries({
      queryKey: getStudentSelfScheduleEligibilityQueryKey({
        path: { id: studentId ?? '', courseEnrollmentId: selectedEnrollment?.id ?? '' },
      }),
      exact: false,
    });
  };

  // mutations
  const enrollStudentInPeriod = useMutation({
    ...enrollStudentInPeriodMutation(),
    onSuccess: (data) => {
      if (data) {
        invalidateSelfScheduleQueries();
        dialogs.open(FeedbackDialog, {
          icon: 'success',
          title: 'You have been enrolled in the course period.',
        });
      }
    },
    onError(error) {
      // get the response data
      const response = error?.response?.data;

      if (response?.error?.code?.startsWith('SSP_VOILATION')) {
        dialogs.open(FeedbackDialog, {
          title: 'Course Policy Error',
          message: response?.error?.message ?? error.message,
        });
      } else {
        dialogs.open(FeedbackDialog, {
          title: 'Error',
          message: response?.error?.message ?? error.message,
        });
      }
    },
  });

  const enrollStudentInPeriodWaitlist = useMutation({
    ...enrollStudentInPeriodWaitlistMutation(),
    onSuccess: (data) => {
      if (data) {
        invalidateSelfScheduleQueries();
        dialogs.open(FeedbackDialog, {
          icon: 'success',
          title: 'You have been added to the waitlist.',
        });
      }
    },
    onError(error) {
      // get the response data
      const response = error?.response?.data;

      dialogs.open(FeedbackDialog, {
        title: 'Error',
        message: response?.error?.message ?? error.message,
      });
    },
  });

  const cancelPeriodEnrollment = useMutation({
    ...cancelPeriodEnrollmentMutation(),
    onSuccess: () => {
      invalidateSelfScheduleQueries();
      dialogs.open(FeedbackDialog, {
        icon: 'success',
        title: 'Course period has been canceled.',
      });
    },
    onError(error) {
      // get the response data
      const response = error?.response?.data;

      dialogs.open(FeedbackDialog, {
        title: 'Error',
        message: response?.error?.message ?? error.message,
      });
    },
  });

  const register = async (id: string, enrollmentType: 'performance_and_observation' | 'observation_only') => {
    await enrollStudentInPeriod.mutateAsync({
      body: {
        coursePeriodId: id,
        studentId: studentId ?? '',
        enrollmentType: enrollmentType,
        overrideRestrictions: false,
      },
    });
  };

  const waitlist = async (id: string, enrollmentType: 'performance_and_observation' | 'observation_only') => {
    // alert(`Waitlist for course period ${id}, ${enrollmentType}`);
    await enrollStudentInPeriodWaitlist.mutateAsync({
      body: {
        coursePeriodId: id,
        studentId: studentId ?? '',
        enrollmentType: enrollmentType,
      },
    });
  };

  const cancel = async (id: string) => {
    await cancelPeriodEnrollment.mutateAsync({
      body: {
        coursePeriodId: id,
        studentId: studentId ?? '',
        cancel: true,
      },
    });
  };

  // handle any errors from fetching the self schedule periods
  if (selfSchedulePeriods.isError) {
    // get the error object
    const error = selfSchedulePeriods.error;

    if (error instanceof ApiError) {
      // showFeedbackDialog({
      //   title: 'Error',
      //   message: error.message
      // });
      console.log('instanceof ApiError: ', error);
    }

    // check if the student has already completed the course
    if (error instanceof ApiError && error.code === 'COURSE_ALREADY_COMPLETED') {
      // return (
      //   <StudentPage title="Register For Course Periods" subtitle="You have already completed all self scheduled courses." />
      // );
    }
  }

  // if the student is not enrolled in a self scheduled courses, show a message
  if (courseEnrollments.status === 'success' && selfScheduleCourseCount === 0) {
    return (
      <StudentPage
        title="Register For Course Periods"
        subtitle="You are currently not enrolled in a self scheduled course. Please contact your driving school to set up an appointment."
      />
    );
  }

  return (
    <StudentPage
      title="Register For Course Periods"
      menuComponent={<SelfScheduleMenu eligibilityBlocks={menuEligibilityBlocks} />}
    >
      <PeriodListHeader
        courseTitle={selectedEnrollment?.course?.title ?? ''}
        courseSectionTitle={selectedEnrollment?.course_section?.title ?? ''}
        selectedDate={selectedDate}
        numOpenings={numOpenings}
        loading={selfSchedulePeriods.isLoading}
      />

      {/* Show time-based eligibility alerts for the selected date */}
      {blocksForSelectedDate.length > 0 && (
        <Stack spacing={2} mb={2}>
          {prioritizeBlocks(blocksForSelectedDate).map((block) => (
            <EligibilityBlockAlert key={block.id} block={block} />
          ))}
        </Stack>
      )}

      <Stack direction="column" spacing={2}>
        {!selfSchedulePeriods.isLoading &&
          selfSchedulePeriodData?.map((period) => {
            // if the student is already enrolled in the course period
            const studentPeriod = studentPeriods?.get(period.id);
            if (studentPeriod && !studentPeriod?.canceled_at) {
              return (
                <StudentPeriod
                  key={studentPeriod?.course_period?.id}
                  id={studentPeriod?.course_period?.id}
                  start={studentPeriod?.course_period?.start}
                  end={studentPeriod?.course_period?.end}
                  showDate={true}
                  courseTitle={studentPeriod?.course_period?.course_section?.course?.title ?? ''}
                  sectionTitle={studentPeriod?.course_period?.course_section?.title}
                  attendance={studentPeriod?.attendance}
                  canceledAt={studentPeriod?.canceled_at}
                  allowCancellation={studentPeriod?.course_enrollment?.course_policy?.allow_cancellations ?? false}
                  lateCancellationDeadline={studentPeriod?.course_enrollment?.course_policy?.late_cancellation_deadline}
                  instructors={studentPeriod?.course_period?.instructors?.map(
                    (instructor) => instructor?.formal_name ?? '',
                  )}
                  location={studentPeriod?.course_period?.course_section?.tenant_location?.name}
                  onClickCancel={(id: string) => {
                    dialogs
                      .confirm('Are you sure you want to cancel this period?', {
                        title: 'Cancel Period',
                        severity: 'error',
                      })
                      .then((confirmed) => {
                        if (confirmed) {
                          cancel(id);
                        }
                      });
                  }}
                />
              );
            }

            return (
              <SelfSchedulePeriod
                key={period?.id}
                id={period?.id}
                start={period?.start}
                end={period?.end}
                openEnrollment={period?.open_enrollment}
                closeEnrollment={period?.close_enrollment}
                capacity={period?.capacity}
                observationCapacity={period?.observation_capacity}
                waitlistCapacity={period?.waitlist_capacity}
                waitlistObservationCapacity={period?.waitlist_observation_capacity}
                enrollmentPerformanceCount={period?.enrollment_performance_count}
                enrollmentObservationCount={period?.enrollment_observation_count}
                waitlistPerformanceCount={period?.waitlist_performance_count}
                waitlistObservationCount={period?.waitlist_observation_count}
                instructors={period?.instructors?.map((instructor) => instructor?.name)}
                location={period?.location}
                showDate={true}
                onClickRegister={register}
                onClickWaitlist={waitlist}
              />
            );
          })}
      </Stack>

      {selfSchedulePeriods.isLoading && (
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!selfSchedulePeriods.isLoading &&
        performanceTimeRemaining.asMinutes() === 0 &&
        observationTimeRemaining.asMinutes() === 0 && (
          <Typography align="center" fontWeight={700}>
            You have already scheduled all required drives.
          </Typography>
        )}

      {!selfSchedulePeriods.isLoading && selfSchedulePeriodData?.length === 0 && (
        <Typography align="center">
          There are no periods available.
          <br />
          Please select another date.
        </Typography>
      )}

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={enrollStudentInPeriod?.isPending || enrollStudentInPeriodWaitlist?.isPending}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </StudentPage>
  );
}

export default SelfSchedule;
